import { useRef, useCallback, useEffect } from 'react';
import haptics from '../utils/haptics';

/**
 * useSwipeGestures - Hook für Swipe-Gesten auf Touch-Geräten
 *
 * @param {Object} options
 * @param {Function} options.onSwipeUp - Callback für Swipe nach oben (Spielen)
 * @param {Function} options.onSwipeDown - Callback für Swipe nach unten (Ziehen/Passen)
 * @param {number} options.threshold - Mindest-Swipe-Distanz in px (default: 50)
 * @param {boolean} options.enabled - Aktiviert/deaktiviert Gesten (default: true)
 * @returns {Object} - Ref für das Element und Event-Handler
 */
export function useSwipeGestures({
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  enabled = true,
} = {}) {
  const touchStartRef = useRef(null);
  const touchCurrentRef = useRef(null);
  const hasTriggeredRef = useRef(false);
  const hasReachedThresholdRef = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;

    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    touchCurrentRef.current = null;
    hasTriggeredRef.current = false;
    hasReachedThresholdRef.current = false;
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;

    const touch = e.touches[0];
    touchCurrentRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };

    // Berechne vertikale Distanz
    const deltaY = touchStartRef.current.y - touch.clientY;

    // Haptic Feedback bei Threshold-Erreichen
    if (!hasReachedThresholdRef.current && Math.abs(deltaY) >= threshold) {
      hasReachedThresholdRef.current = true;
      haptics.swipeThreshold();
    }
  }, [enabled, threshold]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;

    const endPoint = touchCurrentRef.current || {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    const deltaY = touchStartRef.current.y - endPoint.y;
    const deltaX = endPoint.x - touchStartRef.current.x;

    // Mindestens threshold Distanz und mehr vertikal als horizontal
    const isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);
    const isSignificant = Math.abs(deltaY) >= threshold;

    if (isVerticalSwipe && isSignificant && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;

      if (deltaY > 0) {
        // Swipe nach oben - Spielen
        haptics.medium();
        onSwipeUp?.();
      } else {
        // Swipe nach unten - Ziehen/Passen
        haptics.medium();
        onSwipeDown?.();
      }
    }

    // Reset
    touchStartRef.current = null;
    touchCurrentRef.current = null;
  }, [enabled, threshold, onSwipeUp, onSwipeDown]);

  const handleTouchCancel = useCallback(() => {
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    hasTriggeredRef.current = false;
    hasReachedThresholdRef.current = false;
  }, []);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      touchStartRef.current = null;
      touchCurrentRef.current = null;
    };
  }, []);

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel,
    },
  };
}

export default useSwipeGestures;
